import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { TertiaryButton } from 'jl-design-system/elements/button/Button';
import { Message } from 'jl-design-system/components/message/Message';
import { resetForm } from 'jl-design-system/redux/actions/form/formActions';
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';

// Types
import { PaymentType } from 'types/Constants.types';
import { GiftCardVoucherProps } from 'types/GiftCardVoucher.types';
import { PromoCodeProps } from 'types/PromoCode.types';
import { AppDispatch, RootState } from 'types/RootState.types';
import { PaymentDeductionsProps } from './PaymentDeductions.types';
import { PaymentDeductionsListProps } from './PaymentDeductionsList.types';

// Config
import paymentDeductionsState from './PaymentDeductions.state';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { applyGiftCard, removeGiftOption } from '@redux/actions/payment/applyGiftCardActions';
import { applyGiftVoucher } from '@redux/actions/payment/applyGiftVoucherAction';
import {
  closeGiftOptionsModal,
  openGiftOptionsModal,
  changeGiftOption,
  redirectFromGiftCardToPayment,
} from '@redux/actions/payment/paymentActions';
import { applyPromoCode, removePromoCode } from '@redux/actions/payment/promoCodeActions';
import { resetSubmitOrderError } from '@redux/actions/payment/submitOrderActions';
import { giftVouchersSelector } from '@redux/reducers/payment/giftVoucherReducer';
import { APPLY_GIFT_CARD_FORM_ID, APPLY_GIFT_VOUCHER_FORM_ID } from '@utils/form/configs/giftOptions';
import { isZeroAmount } from '@utils/index';
import { isEmptyObject } from '@utils/object';

// Components
import PaymentDeductionsList from './PaymentDeductionsList';
import { GiftOptionsModal } from './gift-options-modal';
import PromoCodeForm from './promo-code-form';
import { BillingDetails } from '@components/add-payment-details/card-details/billing-details';
import Container from '@components/container';
import Heading from '@components/heading';
import RewardsPromoWarning from '@components/rewards-promo-warning/RewardsPromoWarning';

// Styles
import styles from './payment-deductions.scss';

const PaymentDeductions = ({
  applyGiftPaymentFailed = false,
  disabled = false,
  renderingContext = '',
}: PaymentDeductionsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    applyPromoCodeSubmitFailed,
    applyPromoCodeSubmitting,
    getPaymentPageComplete,
    giftApplicationApiCallIsActive,
    giftCards,
    giftOptionBalances,
    isClickCollectOrder,
    modalContentId,
    modalOpen,
    payments,
    outstandingBalance,
    pathname,
    promoCodeApplied,
    promoCodeError,
    promoCodeSubmissionError,
    rewardApplied,
    showRemovePromoCodeErrorMessage,
    silentlyAppliedPromoCode,
    submitOrderError,
    unusedGiftCards,
  } = useSelector((state: RootState) => paymentDeductionsState(state, renderingContext));
  const { desktopResolution } = useWindowSize();
  const giftVouchers = giftVouchersSelector(payments, renderingContext);
  const isPromoCodeApplied = !isEmptyObject(promoCodeApplied ?? {});

  const anyGiftOptionsApplied = giftCards?.length > 0 || giftVouchers?.length > 0;
  const { giftCard, giftVoucher } = giftOptionBalances ?? {};
  const cardNumber = giftCard?.cardNumber ?? '';
  const giftCardRemainingBalance = giftCard?.remainingBalance ?? '';
  const serialNumber = giftVoucher?.serialNumber ?? '';
  const giftVoucherRemainingBalance = giftVoucher?.remainingBalance ?? '';

  const commonProps = useMemo(
    () => ({
      balance: outstandingBalance,
      showRemaining: true,
      hideBalance: true,
      animationDisabled: true,
      isRemovable: true,
    }),
    [outstandingBalance],
  );

  const appliedOptionsConfig: PaymentDeductionsListProps[] = [
    {
      list: giftCards,
      remainingBalance: giftCardRemainingBalance,
      remainingBalanceCardNumber: cardNumber,
      title: 'Gift cards applied',
      type: paymentTypeConstants.GIFT_CARD,
      unusedGiftCards,
      ...commonProps,
    },
    {
      list: giftVouchers,
      remainingBalance: giftVoucherRemainingBalance,
      remainingBalanceCardNumber: serialNumber,
      title: 'Gift vouchers applied',
      infoMessage: null,
      type: paymentTypeConstants.GIFT_VOUCHER,
      ...commonProps,
    },
  ];

  if (isPromoCodeApplied) {
    appliedOptionsConfig.unshift({
      list: [promoCodeApplied],
      title: 'Promo code applied',
      infoMessage: null,
      ...commonProps,
      type: paymentTypeConstants.PROMO_CODE,
      isRemovable: true,
    });
  }

  const [showForm, setShowForm] = useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [promoCodeLoadingCancelled, setPromoCodeLoadingCancelled] = useState<boolean>(false);
  const [promoCodeId, setPromoCodeId] = useState<string>('');
  const applyPromoCodeSubmittingPrev = useRef<boolean>(applyPromoCodeSubmitting);
  const modalCloseHandledRef = useRef<boolean>(false);

  useEffect(() => {
    modalCloseHandledRef.current = false;
  }, [pathname]);

  useEffect(() => {
    if (applyPromoCodeSubmittingPrev.current && !applyPromoCodeSubmitting && !applyPromoCodeSubmitFailed) {
      setShowForm(false);
    }
    applyPromoCodeSubmittingPrev.current = applyPromoCodeSubmitting;
  }, [applyPromoCodeSubmitting, applyPromoCodeSubmitFailed]);

  const onShowModalButtonClick = () => {
    modalCloseHandledRef.current = false;

    if (submitOrderError) {
      dispatch(resetSubmitOrderError());
    }

    dispatch(openGiftOptionsModal());
  };

  const onModalClose = () => {
    if (modalCloseHandledRef.current) return;

    modalCloseHandledRef.current = true;

    dispatch(closeGiftOptionsModal());
    dispatch(resetForm(APPLY_GIFT_CARD_FORM_ID));
    dispatch(resetForm(APPLY_GIFT_VOUCHER_FORM_ID));
  };

  const onRemoveGiftOption = useCallback((item: GiftCardVoucherProps | PromoCodeProps, type?: PaymentType | string) => {
    if (type === paymentTypeConstants.PROMO_CODE) {
      dispatch(removePromoCode(item));
    } else {
      dispatch(removeGiftOption(item));
    }
  }, []);

  const onUndoGiftOptionRemove = useCallback(
    (item: GiftCardVoucherProps, type?: PaymentType | string) => {
      if (type === paymentTypeConstants.GIFT_CARD) {
        const cardNumber = item.details?.cardNumber ?? '';
        const pin = item.details?.pin ?? '';

        dispatch(applyGiftCard({ cardNumber, pin, isUndo: true }));
      }

      if (type === paymentTypeConstants.GIFT_VOUCHER) {
        const serialNumber = item.details?.serialNumber ?? '';
        const securityCode = item.details?.securityCode ?? '';

        dispatch(applyGiftVoucher({ securityCode, serialNumber, isUndo: true }));
      }

      if (type === paymentTypeConstants.PROMO_CODE) {
        const { id = '' } = item;
        if (rewardApplied) {
          setShowWarningModal(true);
          setPromoCodeLoadingCancelled(false);
          setPromoCodeId(id);
          return;
        }

        dispatch(applyPromoCode({ promoCode: id, isUndo: true }));
      }
    },
    [rewardApplied],
  );

  const onShowButtonClick = () => {
    setShowForm(true);
  };

  const orderTotalCovered = isZeroAmount(outstandingBalance);
  const handleGiftOptionsClickHandler = useCallback(() => {
    if (!orderTotalCovered) {
      onShowModalButtonClick();
    }
  }, [orderTotalCovered]);

  const renderUseGiftOptionsButton = () => {
    const isDisabled = disabled || orderTotalCovered;

    const buttonClassnames = cx(styles.giftOptionsCta, {
      [styles.giftOptionsCtaDisabled]: isDisabled,
    });
    const iconClassnames = cx(styles.giftOptionsCtaIcon, {
      [styles.giftOptionsCtaIconDisabled]: isDisabled,
    });

    return (
      <button
        className={buttonClassnames}
        data-testid="gift-options-toggle-button"
        disabled={isDisabled}
        onClick={handleGiftOptionsClickHandler}
        type="button"
      >
        <span className={iconClassnames} />
        <span>Use gift card or voucher</span>
      </button>
    );
  };

  if (!outstandingBalance) {
    return null;
  }

  if (!getPaymentPageComplete) {
    return (
      <Container flex={{ alignItems: 'center', flexDirection: 'col', justifyContent: 'center' }} marginBottom="3">
        <LoadingSpinner />
      </Container>
    );
  }

  const appliedPromoCode = appliedOptionsConfig.find((option) => option.type === paymentTypeConstants.PROMO_CODE);
  const promoCodeAppliedAmount = appliedPromoCode?.list?.[0]?.amount;
  const isRemovePromoErrorVisible = showRemovePromoCodeErrorMessage && !modalOpen;
  const isAutoAppliedPromoMessageVisible = silentlyAppliedPromoCode && promoCodeAppliedAmount;

  const onSubmitFormHandler = ({ promoCode }: { promoCode: string }) => {
    dispatch(applyPromoCode({ promoCode }));
  };
  const onOptionChangeHandler = (contentId: string | null) => {
    dispatch(changeGiftOption(contentId));
  };
  const onRedirectFromGiftCardToPaymentHandler = () => {
    dispatch(redirectFromGiftCardToPayment());
  };
  const onRewardsPromoWarningCancelHandler = () => {
    setShowWarningModal(false);
    setPromoCodeLoadingCancelled(true);
  };
  const onRewardsPromoWarningContinueHandler = () => {
    dispatch(applyPromoCode({ promoCode: promoCodeId }));
    setShowWarningModal(false);
  };

  return (
    <Container background="white" className={styles.container} flex={{ flexDirection: 'col' }} padding="3" width="full">
      {showForm ? (
        <PromoCodeForm
          isSubmitting={applyPromoCodeSubmitting}
          onSubmitForm={onSubmitFormHandler}
          promoCodeError={promoCodeError}
          rewardApplied={rewardApplied}
        />
      ) : (
        <TertiaryButton
          className={styles.promoCodeCta}
          data-testid="apply-promotion-toggle-button"
          disabled={disabled}
          onClick={onShowButtonClick}
          small
        >
          Add a promo code
        </TertiaryButton>
      )}

      {anyGiftOptionsApplied && renderUseGiftOptionsButton()}

      {(isRemovePromoErrorVisible || isAutoAppliedPromoMessageVisible || promoCodeSubmissionError) && (
        <Container marginTop="3">
          {isRemovePromoErrorVisible && (
            <Message
              body="Please try again. If the problem persists, please try again later."
              data-testid="remove-promo-error"
              title="Sorry, there was a technical problem with removing your promo code"
              type="error"
            />
          )}

          {isAutoAppliedPromoMessageVisible && (
            <Message
              body={`We automatically removed ${promoCodeAppliedAmount} from this order`}
              title="Enjoy your savings!"
              type="information"
            />
          )}

          {promoCodeSubmissionError && (
            <Message
              dataId="promo-code-submission-error"
              scrollToMessageOnMount
              title={promoCodeSubmissionError}
              type="error"
            />
          )}
        </Container>
      )}

      {(anyGiftOptionsApplied || isPromoCodeApplied) && (
        <Container
          border="black"
          marginTop="3"
          paddingBottom="3"
          paddingTop={desktopResolution ? '3' : '2'}
          paddingX={desktopResolution ? '3' : '2'}
          testId="gift-option-applied-section"
        >
          <div className={styles.giftOptionsAppliedDeductionsContainer}>
            {appliedOptionsConfig.map((props) => (
              <PaymentDeductionsList
                key={props.title}
                applyGiftPaymentFailed={applyGiftPaymentFailed}
                {...props}
                onRemove={onRemoveGiftOption}
                onUndo={onUndoGiftOptionRemove}
                orderTotalCovered={orderTotalCovered}
                promoCodeLoadingCancelled={promoCodeLoadingCancelled}
                showUndo
              />
            ))}
          </div>

          <Heading tag="div" type="s">
            <Container
              borderTop="grey-15"
              flex={{ flexDirection: 'row', justifyContent: 'space-between' }}
              paddingTop="2"
              testId="gift-options-list-balance"
            >
              <span>Balance to pay</span>
              <span>{outstandingBalance}</span>
            </Container>
          </Heading>
        </Container>
      )}

      {!anyGiftOptionsApplied && renderUseGiftOptionsButton()}

      <GiftOptionsModal
        giftApplicationApiCallIsActive={giftApplicationApiCallIsActive}
        handleModalClose={onModalClose}
        isOpen={modalOpen}
        modalContentId={modalContentId}
        onOptionChange={onOptionChangeHandler}
        onRemove={onRemoveGiftOption}
        onUndo={onUndoGiftOptionRemove}
        redirectFromGiftCardToPayment={onRedirectFromGiftCardToPaymentHandler}
      />

      {orderTotalCovered && (
        <Container marginTop="3">
          <Message dataTestId="no-further-payment-message" title="No further payment is required" type="information" />
        </Container>
      )}

      {isClickCollectOrder && orderTotalCovered && (
        <Container marginTop="5" testId="billing-details-container">
          <Heading label="Your contact details" marginBottom="2" tag="h2" type="s" />
          <p>Please let us know who to contact in case there are any problems with your order</p>
          <BillingDetails hideTitle />
        </Container>
      )}

      {showWarningModal && (
        <RewardsPromoWarning
          direction="promo-to-reward"
          onCancel={onRewardsPromoWarningCancelHandler}
          onContinue={onRewardsPromoWarningContinueHandler}
        />
      )}
    </Container>
  );
};

export default PaymentDeductions;
